import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '@expat-ui/shared';
import { getFreshAccessToken } from '../utils/authUtils';
import {
    UserProfile,
    AssetAccount,
    HouseholdResponse,
    DynamicCashFlow,
    TaxAnalysisResult,
    Goal,
    OnboardingChecklist,
    Country,
} from '../types/api.types';

const ALL_COUNTRY_OPTIONS: { value: Country; label: string }[] = [
    { value: 'INDIA', label: 'India' },
    { value: 'US', label: 'United States' },
    { value: 'CAN', label: 'Canada' },
];

function useApiCall<T>(fetcher: (token: string) => Promise<T>) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const execute = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            const result = await fetcher(token);
            setData(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Request failed');
        } finally {
            setLoading(false);
        }
    }, [fetcher]);

    useEffect(() => {
        execute();
    }, [execute]);

    return { data, loading, error, refetch: execute };
}

export function useProfile() {
    const fetcher = useCallback((token: string) => api.get<UserProfile>('/profile', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);

    const updateProfile = async (profile: Partial<UserProfile>) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.put<UserProfile>('/profile', profile, token);
        refetch();
        return result;
    };

    return { profile: data, loading, error, refetch, updateProfile };
}

export function useAssets() {
    const fetcher = useCallback((token: string) => api.get<AssetAccount[]>('/assets', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);

    const addAsset = async (asset: Omit<AssetAccount, 'accountId' | 'memberSub' | 'updatedAt' | 'pk' | 'sk'>) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.post<AssetAccount>('/assets', asset, token);
        refetch();
        return result;
    };

    const updateAsset = async (
        accountId: string,
        patch: Partial<Pick<AssetAccount, 'balance' | 'expectedReturn' | 'ownership' | 'linkedGoalId'>>,
    ) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.put<AssetAccount>(`/assets/${accountId}`, patch, token);
        refetch();
        return result;
    };

    const deleteAsset = async (accountId: string) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        await api.delete(`/assets/${accountId}`, token);
        refetch();
    };

    return { assets: data || [], loading, error, refetch, addAsset, updateAsset, deleteAsset };
}

export function useHousehold() {
    const fetcher = useCallback((token: string) => api.get<HouseholdResponse>('/household', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);

    const createHousehold = async (name: string) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.post<HouseholdResponse>('/household', { name }, token);
        refetch();
        return result;
    };

    return { household: data, loading, error, refetch, createHousehold };
}

export function useCashflows() {
    const fetcher = useCallback((token: string) => api.get<DynamicCashFlow[]>('/cashflows', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);

    const addCashflow = async (cashflow: Omit<DynamicCashFlow, 'cashFlowId' | 'memberSub' | 'updatedAt'>) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.post<DynamicCashFlow>('/cashflows', cashflow, token);
        refetch();
        return result;
    };

    const updateCashflow = async (
        cashFlowId: string,
        patch: Partial<Omit<DynamicCashFlow, 'cashFlowId' | 'memberSub' | 'updatedAt'>>,
    ) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.put<DynamicCashFlow>(`/cashflows/${cashFlowId}`, patch, token);
        refetch();
        return result;
    };

    const deleteCashflow = async (cashFlowId: string) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        await api.delete(`/cashflows/${cashFlowId}`, token);
        refetch();
    };

    return { cashflows: data || [], loading, error, refetch, addCashflow, updateCashflow, deleteCashflow };
}

export function useGoals() {
    const fetcher = useCallback((token: string) => api.get<Goal[]>('/goals', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);
    return { goals: data || [], loading, error, refetch };
}

export function useTaxAnalysis() {
    const fetcher = useCallback((token: string) => api.get<TaxAnalysisResult>('/tax', token), []);
    return useApiCall<TaxAnalysisResult>(fetcher);
}

export function useResidencyCountries() {
    const { profile, loading } = useProfile();

    const countries = useMemo<Country[]>(() => {
        if (!profile?.residencyHistory?.length) return [];
        return [...new Set(profile.residencyHistory.map((r) => r.country))] as Country[];
    }, [profile]);

    const countryOptions = useMemo(
        () =>
            countries.length > 0 ? ALL_COUNTRY_OPTIONS.filter((c) => countries.includes(c.value)) : ALL_COUNTRY_OPTIONS,
        [countries],
    );

    const retirementCountry = useMemo<Country | null>(() => {
        if (!profile?.residencyHistory?.length) return null;
        const retAge = profile.targetRetirementAge;
        const history = profile.residencyHistory;
        const atRetirement = history.find((r) => retAge >= r.startAge && retAge <= r.endAge);
        if (atRetirement) return atRetirement.country as Country;
        const sorted = [...history].sort((a, b) => b.endAge - a.endAge);
        return (sorted[0]?.country ?? null) as Country | null;
    }, [profile]);

    return { countries, countryOptions, retirementCountry, loading };
}

export function useFeedback() {
    const submitFeedback = async (payload: { type: 'BUG' | 'FEATURE_REQUEST'; title: string; description: string }) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        return api.post<{ feedbackId: string }>('/feedback', payload, token);
    };

    return { submitFeedback };
}

export function useOnboardingChecklist() {
    const fetcher = useCallback((token: string) => api.get<OnboardingChecklist>('/onboarding/checklist', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);

    const skipSection = async (sectionId: string) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        await api.put(`/onboarding/sections/${sectionId}/skip`, {}, token);
        refetch();
    };

    const markTimelineComplete = async () => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        await api.put('/onboarding/timeline-complete', {}, token);
    };

    return { checklist: data, loading, error, refetch, skipSection, markTimelineComplete };
}
